import { ExpenseItem } from '../../services/ExpenseService';
import { CRITICAL_ITEMS_MAPPING, TIER_THRESHOLDS } from './constants';

// Re-export CRITICAL_ITEMS_MAPPING for use in Expenses.tsx
export { CRITICAL_ITEMS_MAPPING };

export interface CriticalItem {
  criticalItemName: string;
  totalQuantity: number;
  totalAmount: number;
}

// Get tier for a location based on weekly sales
export const getTierForLocation = (sales: number, isQuarterly: boolean): string => {
  const weeklySales = isQuarterly ? sales / 13 : sales;
  if (weeklySales >= 35000) return 'HIGH_VOLUME';
  if (weeklySales >= 25000) return 'UPPER_MID';
  if (weeklySales >= 20000) return 'LOWER_MID';
  return 'BASE';
};

// Map dashboard tier names to tier constants (dashboard may use different format)
export const mapDashboardTierNameToTierConstant = (dashboardTierName: string): string => {
  const upperName = dashboardTierName.toUpperCase().trim();
  
  // Exact matches first
  if (upperName === 'HIGH_VOLUME' || upperName === 'HIGH VOLUME') return 'HIGH_VOLUME';
  if (upperName === 'UPPER_MID' || upperName === 'UPPER MID') return 'UPPER_MID';
  if (upperName === 'LOWER_MID' || upperName === 'LOWER MID') return 'LOWER_MID';
  if (upperName === 'BASE') return 'BASE';
  
  // Pattern matching
  if (upperName.includes('HIGH') && upperName.includes('VOLUME')) return 'HIGH_VOLUME';
  if (upperName.includes('UPPER') && upperName.includes('MID')) return 'UPPER_MID';
  if (upperName.includes('LOWER') && upperName.includes('MID')) return 'LOWER_MID';
  
  // If it just says "MID" without upper/lower, we can't determine - default to BASE
  if (upperName.includes('MID') && !upperName.includes('UPPER') && !upperName.includes('LOWER')) {
    console.warn(`Unclear tier name: "${dashboardTierName}" - defaulting to BASE`);
    return 'BASE';
  }
  
  return 'BASE';
};

// Get dynamic threshold for a location (tier baseline + 1.5%)
export const getDynamicThreshold = (
  location: string,
  totalSales: number,
  isQuarterly: boolean,
  tierBaselineGfsPercent: Record<string, number>,
  locationTierMap: Record<string, string>
): { high: number; medium: number } => {
  // For weekly view, use tier from dashboard data if available
  let tier: string;
  if (!isQuarterly && locationTierMap[location]) {
    tier = locationTierMap[location];
  } else {
    // Fallback to calculating tier from sales
    tier = getTierForLocation(totalSales, isQuarterly);
  }
  
  // For weekly view, use dynamic threshold based on tier's baseline average 8-week GFS %
  if (!isQuarterly && tierBaselineGfsPercent[tier] !== undefined) {
    const tierBaselineGfs = tierBaselineGfsPercent[tier];
    const dynamicHigh = tierBaselineGfs + 1.5;
    const dynamicMedium = tierBaselineGfs + 0.5; // Medium threshold is 0.5% below high
    return { high: dynamicHigh, medium: dynamicMedium };
  }
  
  // Fallback to static tier-based thresholds for quarterly view or when baseline data is unavailable
  return TIER_THRESHOLDS[tier];
};

// Group items by critical item name and aggregate quantities and costs
export const groupItemsByCriticalItem = (items: ExpenseItem[]): CriticalItem[] => {
  const criticalItemsMap = new Map<string, { quantity: number; amount: number }>();

  items.forEach((item) => {
    const criticalItemName = CRITICAL_ITEMS_MAPPING[item.itemName] || 'Other';
    const existing = criticalItemsMap.get(criticalItemName) || { quantity: 0, amount: 0 };
    criticalItemsMap.set(criticalItemName, {
      quantity: existing.quantity + item.billedQuantity,
      amount: existing.amount + item.totalAmount,
    });
  });

  return Array.from(criticalItemsMap.entries())
    .map(([criticalItemName, { quantity, amount }]) => ({
      criticalItemName,
      totalQuantity: quantity,
      totalAmount: amount,
    }))
    .sort((a, b) => a.criticalItemName.localeCompare(b.criticalItemName));
};

// Helper function to calculate average quantity for a critical item
export const calculateCriticalItemAvgQty = (
  criticalItem: CriticalItem,
  items: ExpenseItem[],
  averages: Record<string, { averageQuantity: number; averageExpense: number }>
): number => {
  const matchingItems = items.filter(item => {
    const criticalItemName = CRITICAL_ITEMS_MAPPING[item.itemName] || 'Other';
    return criticalItemName === criticalItem.criticalItemName;
  });
  
  let totalAvgQty = 0;
  let foundAny = false;
  matchingItems.forEach(item => {
    const avg = averages[item.itemName];
    if (avg) {
      totalAvgQty += avg.averageQuantity;
      foundAny = true;
    }
  });
  
  return foundAny ? totalAvgQty : 0;
};

// Helper function to calculate average expense for a critical item
export const calculateCriticalItemAvgExpense = (
  criticalItem: CriticalItem,
  items: ExpenseItem[],
  averages: Record<string, { averageQuantity: number; averageExpense: number }>
): number => {
  const matchingItems = items.filter(item => {
    const criticalItemName = CRITICAL_ITEMS_MAPPING[item.itemName] || 'Other';
    return criticalItemName === criticalItem.criticalItemName;
  });
  
  let totalAvgExpense = 0;
  let foundAny = false;
  matchingItems.forEach(item => {
    const avg = averages[item.itemName];
    if (avg) {
      totalAvgExpense += avg.averageExpense;
      foundAny = true;
    }
  });
  
  return foundAny ? totalAvgExpense : 0;
};

// Helper function to find sales for a location (case-insensitive and trimmed matching)
export const getSalesForLocation = (
  locationName: string,
  salesByLocation: Record<string, number>
): number => {
  if (!locationName) return 0;
  
  const normalizedLocation = locationName.trim();
  
  // Try exact match first
  if (salesByLocation[normalizedLocation] !== undefined) {
    return salesByLocation[normalizedLocation];
  }
  
  // Try case-insensitive match
  const lowerLocation = normalizedLocation.toLowerCase();
  for (const [salesLocation, amount] of Object.entries(salesByLocation)) {
    const normalizedSalesLocation = salesLocation.trim();
    if (normalizedSalesLocation.toLowerCase() === lowerLocation) {
      return amount;
    }
    // Also try without trimming for edge cases
    if (salesLocation.toLowerCase() === lowerLocation) {
      return amount;
    }
  }
  
  // Log if no match found (for debugging)
  if (Object.keys(salesByLocation).length > 0) {
    console.debug(`No sales match found for location: "${locationName}". Available locations:`, 
      Object.keys(salesByLocation).slice(0, 5));
  }
  
  return 0;
};

